<div class="mx-auto flex max-w-[1400px] flex-col gap-5">

  <!-- HERO CARD -->
  <div class="rounded-2xl border border-border bg-card p-5 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="flex min-w-0 flex-col gap-1">
        <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
          Tariff Check · Customer {{ session.customerId() }}
        </p>
        <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
          Rate Comparison
        </h1>
        <p class="text-sm text-muted-foreground">
          Current plan: <strong class="font-mono-feat text-foreground">{{ currentPlanName() }}</strong>
        </p>
      </div>
    </div>
  </div>

  @if (!hasPhoneService()) {
    <hlm-alert tone="warn">
      <hlm-icon name="lucideTriangleAlert" size="1rem" class="mt-0.5" />
      <span>No phone service found. Tariff check not possible.</span>
    </hlm-alert>
  }

  @if (loaded() && noActivePhoneserviceTariff()) {
    <hlm-alert tone="info">
      <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5" />
      <span>
        For the current tariff is no tariff-check possible.
        Because this tariff didn't contain phoneservice activities.
        Current tariff: <strong>'{{ currentPlanName() }}'</strong>.
      </span>
    </hlm-alert>
  }

  @if (hasPhoneService() && !noActivePhoneserviceTariff()) {
    @if (loaded() && isAutoTariff()) {
      <hlm-alert tone="warn">
        <hlm-icon name="lucideTriangleAlert" size="1rem" class="mt-0.5" />
        <span>Current plan is auto-tariff ({{ currentPlanName() }}). No comparison available.</span>
      </hlm-alert>
    }

    @if (loaded()) {
      <!-- Extension toggles -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideSlidersHorizontal" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Extension State</h2>
          <span class="text-sm text-muted-foreground">(customizable)</span>
        </header>

        <div class="flex flex-col gap-2">
          @for (ext of extensions(); track ext.name) {
            <div class="flex items-center gap-3">
              <hlm-checkbox
                [checked]="ext.booked"
                (checkedChange)="toggleExtension(ext)"
                [label]="ext.showName"
                [disabled]="loading() || !!ext.error"
              />
              @if (ext.error) {
                <span class="text-sm text-destructive">{{ ext.error }}</span>
              }
            </div>
          }
        </div>

        <!-- Comparison period -->
        <div class="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <span class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em]">Comparison period</span>
          <span class="text-muted-foreground/40">|</span>
          <span class="font-mono-feat text-foreground">{{ comparisonPeriodFrom() }}</span>
          <span>to</span>
          <span class="font-mono-feat text-foreground">{{ comparisonPeriodTo() }}</span>
        </div>
      </section>
    }

    @if (currentPlan() || bestPlan()) {
      <!-- Compare results overview -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideArrowRight" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Compare Results</h2>
        </header>

        <div class="grid gap-4 sm:grid-cols-2">
          @if (isAutoTariff()) {
            <div class="rounded-xl border border-border bg-muted/30 p-4">
              <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Current</p>
              <p class="mt-1 font-display text-lg font-semibold text-foreground">{{ currentPlanName() }}</p>
            </div>
          } @else if (currentPlan(); as plan) {
            <div class="rounded-xl border border-border bg-muted/30 p-4">
              <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Current</p>
              <p class="mt-1 font-display text-lg font-semibold text-foreground">
                {{ plan.localizedName ?? plan.planName }}
              </p>
              <p class="mt-1 text-sm text-muted-foreground">{{ plan.extensionsText }}</p>
              <p class="mt-2 font-mono-feat text-sm text-foreground">
                {{ plan.totalSum | number:'1.2-2' }} {{ currency() }}
                <span class="ml-1 text-sm text-muted-foreground">
                  (basic: {{ plan.totalBasicFeeSum | number:'1.2-2' }} + usage: {{ plan.totalServiceFeeSum | number:'1.2-2' }})
                </span>
              </p>
            </div>
          }

          @if (bestPlan(); as plan) {
            <div class="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
              <div class="flex items-center gap-2">
                <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-primary">Best</p>
                <hlm-badge variant="success">Recommended</hlm-badge>
              </div>
              <p class="mt-1 font-display text-lg font-semibold text-foreground">
                {{ plan.localizedName ?? plan.planName }}
              </p>
              <p class="mt-1 text-sm text-muted-foreground">{{ plan.extensionsText }}</p>
              <p class="mt-2 font-mono-feat text-sm text-foreground">
                {{ plan.totalSum | number:'1.2-2' }} {{ currency() }}
                <span class="ml-1 text-sm text-muted-foreground">
                  (basic: {{ plan.totalBasicFeeSum | number:'1.2-2' }} + usage: {{ plan.totalServiceFeeSum | number:'1.2-2' }})
                </span>
              </p>
            </div>
          }
        </div>
      </section>
    }

    @if (loading()) {
      <div class="flex items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
        <hlm-spinner size="24" />
        Loading tariff data…
      </div>
    }

    @if (loaded() && !loading() && !isAutoTariff() && sortedPlans().length > 0) {
      <!-- Detailed overview (AG-Grid) -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideFileText" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Detailed Overview</h2>
        </header>

        <div class="overflow-hidden rounded-xl border border-border">
          <ag-grid-angular
            class="ag-theme-alpine"
            [rowData]="rowData()"
            [columnDefs]="columnDefs()"
            [defaultColDef]="defaultColDef"
            [pinnedBottomRowData]="pinnedBottomRowData()"
            [domLayout]="'autoHeight'"
            [rowHeight]="28"
            [headerHeight]="32"
            (firstDataRendered)="onFirstDataRendered($event)"
            (gridSizeChanged)="onGridSizeChanged($event)"
          />
        </div>
      </section>
    }
  }

</div>
